import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Mail, CheckCircle2 } from "lucide-react";
import CoreLogo from "@/components/CoreLogo";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      // Use bare axios — no cookies needed for public reset endpoints.
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-center items-center px-6 py-12 bg-white" data-testid="forgot-password-page">
      <div className="max-w-md w-full">
        <div className="mb-8 text-neutral-900" data-testid="forgot-core-logo">
          <CoreLogo variant="inline" size={42} />
        </div>
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-neutral-500 mb-3">Account Recovery</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-neutral-900 mb-3">Forgot your password?</h1>

        {!sent ? (
          <>
            <p className="text-sm text-neutral-500 mb-10">
              Enter the email address on your account. We&apos;ll send you a magic link to reset your password. The link expires in 60 minutes.
            </p>
            <form onSubmit={submit} className="space-y-5" data-testid="forgot-password-form">
              <div>
                <Label htmlFor="email" className="text-xs font-sans uppercase tracking-[0.2em] text-neutral-500">Email</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-2 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900"
                  data-testid="forgot-email-input"
                />
              </div>
              {error && <p className="text-sm text-red-600" data-testid="forgot-error">{error}</p>}
              <Button
                type="submit"
                disabled={submitting}
                className="w-full rounded-none bg-neutral-900 hover:bg-neutral-800 text-white h-11 font-sans tracking-wide"
                data-testid="forgot-submit-button"
              >
                <Mail className="h-4 w-4 mr-2" strokeWidth={1.5} />
                {submitting ? "Sending…" : "Send reset link"}
              </Button>
            </form>
          </>
        ) : (
          <div data-testid="forgot-password-sent">
            <div className="border border-neutral-200 p-6 mb-6 flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-emerald-700 mt-0.5 shrink-0" strokeWidth={1.5} />
              <div>
                <div className="font-serif text-xl mb-2">Check your inbox.</div>
                <p className="text-sm text-neutral-600 leading-relaxed">
                  If an account exists for <span className="font-mono text-neutral-900">{email}</span>, a reset link is on its way. It expires in 60 minutes.
                </p>
                <p className="text-xs text-neutral-500 mt-3">
                  Didn&apos;t get it? Check spam, or{" "}
                  <button
                    type="button"
                    onClick={() => { setSent(false); setEmail(""); }}
                    className="text-neutral-900 underline underline-offset-2"
                    data-testid="forgot-resend-button"
                  >
                    try again
                  </button>.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="mt-10 pt-6 border-t border-neutral-200 text-sm">
          <Link to="/login" className="inline-flex items-center gap-2 text-neutral-500 hover:text-neutral-900" data-testid="forgot-back-to-login">
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
